import { Phone, Zap, type LucideIcon } from "lucide-react";
import { DelegationTask } from "protogen/delegations_pb";

export const LIGHTNING_DETECTION_STAGE = "lightning-detection";

export const TASK_DELETE_DISABLED_REASON =
  "This task has active subtasks. Complete or cancel them before deleting.";

export const canDeleteTask = (
  delegationTasks: DelegationTask[] = [],
): boolean =>
  !delegationTasks.some(
    (dt) => dt.status !== "completed" && dt.status !== "cancelled",
  );
export const LIGHTNING_TASK_TYPE_PHONE_CALLS = "phone-calls";

type LightningTaskTypeDefinition = {
  value: string;
  title: string;
  description: string;
  icon: LucideIcon;
  supportsDirectPhoneActions: boolean;
  memberSuggestionTitle?: string;
  memberSuggestionDescription?: string;
};

const DEFAULT_LIGHTNING_TASK_TYPE: LightningTaskTypeDefinition = {
  value: "generic",
  title: "Lightning Task",
  description: "General delegated task support.",
  icon: Zap,
  supportsDirectPhoneActions: false,
};

export const LIGHTNING_TASK_TYPES: Record<string, LightningTaskTypeDefinition> =
  {
    [LIGHTNING_TASK_TYPE_PHONE_CALLS]: {
      value: LIGHTNING_TASK_TYPE_PHONE_CALLS,
      title: "Phone Calls",
      description: "Enables delegated phone call capabilities.",
      icon: Phone,
      supportsDirectPhoneActions: true,
      memberSuggestionTitle: "Phone call",
      memberSuggestionDescription:
        "Best for simple calls you want handled quickly.",
    },
  };

export const getLightningTaskTypeDefinition = (
  serviceType?: string,
): LightningTaskTypeDefinition =>
  (serviceType && LIGHTNING_TASK_TYPES[serviceType]) ||
  DEFAULT_LIGHTNING_TASK_TYPE;

export const isPhoneCallLightningTask = (serviceType?: string): boolean =>
  serviceType === LIGHTNING_TASK_TYPE_PHONE_CALLS;

type DelegationFunctionCallData = {
  id: string;
  name: string;
  args?: {
    title?: string;
    description?: string;
    details?: Array<{
      name: string;
      value: string;
      detailType?: string;
      entityType?: string;
    }>;
    future_action_date?: string;
    time_estimate_minutes?: number;
    service_type?: string;
  };
};

export type ParsedDelegationOpportunity = {
  functionId: string;
  title: string;
  description: string;
  details: Array<{
    name: string;
    value: string;
    detailType?: string;
    entityType?: string;
  }>;
  status: "pending";
  serviceType: string;
  actionDateSec?: number;
  timeEstimateMinutes?: number;
};

export const isDelegationFunctionCallName = (name?: string): boolean =>
  !!name && name.startsWith("delegate_");

export const parseDelegationOpportunityFromFunctionCall = (
  functionCallData: DelegationFunctionCallData,
): ParsedDelegationOpportunity | null => {
  if (!isDelegationFunctionCallName(functionCallData.name)) {
    return null;
  }

  const serviceType = functionCallData.args?.service_type || "";

  if (!serviceType) {
    return null;
  }

  let actionDateSec: number | undefined;
  if (functionCallData.args?.future_action_date) {
    actionDateSec = Math.floor(
      new Date(functionCallData.args.future_action_date).getTime() / 1000,
    );
  }

  return {
    functionId: functionCallData.id,
    title: functionCallData.args?.title || "",
    description: functionCallData.args?.description || "",
    details: functionCallData.args?.details || [],
    status: "pending",
    serviceType,
    actionDateSec,
    timeEstimateMinutes: functionCallData.args?.time_estimate_minutes,
  };
};
